import type { GetState, SetState } from "stores/types";
import { expandAll } from "./actions/expandAll";
import type { TreeApi } from "@hogartrabe/react-arborist-vrh";
import type { Node } from "interfaces/schemas";
import type { ProjectUIStore } from "./projectUIStore";
import { getTreeApi } from "../project/projectStore";
import { collapseAll } from "./actions/collapseAll";
import { collapseLevel } from "./actions/collapseLevel";
import { expandLevel } from "./actions/expandLevel";
import { toggleNode } from "./actions/toggleNode";
import { setRedrawIndex } from "./actions/setRedrawIndex";
import { redrawList } from "./actions/redrawList";

type SetShowProductPicturesArgs = boolean | ((prev: boolean) => boolean);

export type ProjectUIActions = {
  expandAll: () => void;
  collapseAll: () => void;
  expandLevel: () => void;
  collapseLevel: () => void;
  toggleNode: (nodeId: string) => void;
  setShowProductPictures: (
    showProductPictures: SetShowProductPicturesArgs,
  ) => void;
  setRedrawIndex: (index: number) => void;
  redrawList: () => void;
};

export const createProjectUIActions = (
  set: SetState<ProjectUIStore>,
  get: GetState<ProjectUIStore>,
): ProjectUIActions => ({
  expandAll: () =>
    runOpenMapAction(({ treeApi }) => expandAll(treeApi), set, get),
  collapseAll: () =>
    runOpenMapAction(({ treeApi }) => collapseAll(treeApi), set, get),
  expandLevel: () =>
    runOpenMapAction(
      ({ treeApi, openMap }) => expandLevel(openMap, treeApi),
      set,
      get,
    ),
  collapseLevel: () =>
    runOpenMapAction(
      ({ treeApi, openMap }) => collapseLevel(openMap, treeApi),
      set,
      get,
    ),
  toggleNode: (nodeId: string) =>
    runOpenMapAction(({ openMap }) => toggleNode(openMap, nodeId), set, get),
  setShowProductPictures: (showProductPictures) =>
    set({
      showProductPictures:
        typeof showProductPictures === "function"
          ? showProductPictures(get().showProductPictures)
          : showProductPictures,
    }),
  setRedrawIndex: (index: number) =>
    runRedrawAction(() => setRedrawIndex(index), set, get),
  redrawList: () =>
    runRedrawAction(
      ({ redrawIndex, treeApi }) => redrawList(treeApi, redrawIndex),
      set,
      get,
    ),
});

const runRedrawAction = (
  actionFn: ({
    redrawIndex,
    treeApi,
  }: {
    redrawIndex: number;
    treeApi: TreeApi<Node>;
  }) => number,
  set: SetState<ProjectUIStore>,
  get: GetState<ProjectUIStore>,
) => {
  try {
    // Get the current project, tree, and language from the store
    const redrawIndex = get().redrawIndex;
    if (redrawIndex === undefined) {
      throw new Error("Redraw index is not initialized");
    }
    const treeApi = getTreeApi();
    if (!treeApi) {
      throw new Error("Tree API is not initialized");
    }
    // Call the action function with the current state
    const index = actionFn({ redrawIndex, treeApi });
    set({ redrawIndex: index });
  } catch (error) {
    console.error("An error occurred whilst performing an action:", error);
  }
};

const runOpenMapAction = (
  actionFn: ({
    openMap,
    treeApi,
  }: {
    openMap: Record<string, boolean> | null;
    treeApi: TreeApi<Node>;
  }) => Record<string, boolean> | null,
  set: SetState<ProjectUIStore>,
  get: GetState<ProjectUIStore>,
) => {
  try {
    // Get the current project, tree, and language from the store
    const openMap = get().openMap;
    if (!openMap) {
      throw new Error("Open map is not initialized");
    }
    const treeApi = getTreeApi();
    if (!treeApi) {
      throw new Error("Tree API is not initialized");
    }
    // Call the action function with the current state
    const newOpenMap = actionFn({ openMap, treeApi });
    if (newOpenMap) {
      set({ openMap: newOpenMap });
    }
  } catch (error) {
    console.error("An error occurred whilst performing an action:", error);
  }
};
